import React, { useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAppConfig } from "../../context/AppConfigContext";
import { useTranslation } from "../../i18n/LanguageContext";
import LanguageSwitcher from "../LanguageSwitcher";
import PWAInstallPrompt from "../PWAInstallPrompt";
import { usePwaInstall, promptInstall, requestInstallPopup } from "../../utils/pwaInstall";
import { LogOut, User, UserCircle, Building2, CreditCard, Crown, Menu, X, Download } from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const { appName, branding } = useAppConfig();
  const { t, i18nEnabled } = useTranslation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { canInstall, isIOS } = usePwaInstall();
  const [installIconFailed, setInstallIconFailed] = useState(false);

  const handleInstallApp = () => {
    setSidebarOpen(false);
    // iOS has no install dialog: show the pop-up with the Share instructions
    if (isIOS) requestInstallPopup();
    else promptInstall();
  };

  const navLinks = [
    { to: "/app/profiles", label: t("layout.profiles"), icon: UserCircle },
    { to: "/app/cards", label: t("layout.cards"), icon: CreditCard },
    { to: "/app/subscriptions", label: t("layout.subscriptions"), icon: Crown },
    { to: "/app/organizations", label: t("layout.organizations"), icon: Building2 },
    { to: "/app/account", label: t("layout.account"), icon: User },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div id="app-root" className="min-h-screen bg-gray-50">
      {/* Navbar - fixed */}
      <header id="app-header" className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 h-14">
        <div className="flex items-center justify-between px-4 h-14">
          {/* Left: hamburger + brand */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen((prev) => !prev)}
              className="md:hidden p-1.5 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/app" className="flex items-center gap-2 text-xl font-bold text-gray-900 tracking-tight">
              <img
                src={branding.logo}
                alt={appName}
                className="h-8"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="sr-only">{appName}</span>
            </Link>
          </div>

          {/* Right: language switcher + user info + logout */}
          <div className="flex items-center gap-4">
            {i18nEnabled && <LanguageSwitcher />}
            {user?.email && (
              <span className="hidden sm:inline text-sm text-gray-600">{user.email}</span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-600 transition-colors focus:outline-none"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t("layout.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      <div className="flex flex-1">
        {/* Sidebar overlay on mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-[44] md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          id="app-sidebar"
          className={`
            fixed md:fixed inset-y-0 left-0 z-[45] w-56 bg-white border-r border-gray-200
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
            md:translate-x-0 md:flex md:flex-col
            pt-14
          `}
        >
          <nav id="app-sidebar-nav" className="flex flex-col gap-1 p-3">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <Icon size={18} />
                {label}
              </Link>
            ))}
            {canInstall && (
              <button
                onClick={handleInstallApp}
                className="flex items-center gap-2 px-3 py-2 mt-2 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
              >
                {branding?.icon && !installIconFailed ? (
                  <img
                    src={branding.icon}
                    alt=""
                    onError={() => setInstallIconFailed(true)}
                    className="w-[18px] h-[18px] rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <Download size={18} />
                )}
                {t("layout.installApp")}
              </button>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <main id="app-main" className="flex-1 min-w-0 md:ml-56 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
      <PWAInstallPrompt />
    </div>
  );
}
